import React, { useMemo } from "react";

import { Release } from "../types";

import DateTimeFormatter from "@/common/DateTimeFormatter/DateTimeFormatter";

import Icon from "@/assets/images/icons";
import { MoreOutlined } from "@ant-design/icons";
import { Checkbox, Dropdown } from "antd";

import Text from "@/common/Text/Text";
import styles from "./ReleaseRow.module.scss";

import { ActiveModalNameEnum } from "@/features/ui/types";
import { setActiveModal } from "@/features/ui/uiSlice";
import { useDispatch } from "react-redux";

interface ReleaseRowProps extends Release {
  checked: boolean;
  onChangeCheckbox: (event: any, release: Release) => void;
}

const ReleaseRow = (props: ReleaseRowProps) => {
  const { checked, onChangeCheckbox, ...release } = props;
  const {
    created_at,
    createdAt: createdAtField,
    description,
    type,
    version,
    environment,
  } = release;
  const createdAt = createdAtField || created_at || "";

  const dispatch = useDispatch();

  const handleOnchangeCheckbox = (event: any) =>
    onChangeCheckbox(event, release);

  const handleMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case "makeActive":
        dispatch(
          setActiveModal({
            name: ActiveModalNameEnum.ReleaseMakeActive,
            ids: [release.id],
          })
        );
        break;
      case "download":
        dispatch(setActiveModal({ name: ActiveModalNameEnum.ReleaseDownload }));
        break;
      case "delete":
        dispatch(setActiveModal({ name: ActiveModalNameEnum.ReleaseDelete }));
        break;
      case "sendToProd":
        dispatch(
          setActiveModal({
            name: ActiveModalNameEnum.ReleaseSendToProduction,
            ids: [release.id],
          })
        );
        break;
    }
  };

  const menuItems = useMemo(() => {
    const allMenuItems = [
      {
        key: "makeActive",
        icon: <Icon name="makeActive" size={16} />,
        label: "Make Active",
      },
      {
        key: "sendToProd",
        icon: <Icon name="sendToProd" size={24} />,
        label: "Send to Production",
      },
      {
        key: "download",
        icon: <Icon name="download" size={16} />,
        label: "Download",
      },
      {
        key: "delete",
        icon: <Icon name="trash" size={16} />,
        label: "Delete",
      },
    ];
    return allMenuItems.filter(
      (item) =>
        item.key !== "sendToProd" || release.environment !== "production"
    );
  }, [release.environment]);

  const dropdownMenu = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  // Parse release notes from description
  const renderDescription = () => {
    if (!description) return null;

    return (
      <div>
        <div className={styles.releaseNotesTitle}>
          <Text size="sm">Release Notes:</Text>
        </div>
        <ul className={styles.descriptionList}>
          {description.split("\n").map((item, index) => (
            <li key={index}>
              <Text>{item.trim()}</Text>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <tr className={styles.row}>
      <td>
        <Checkbox checked={checked} onChange={handleOnchangeCheckbox} />
      </td>
      <td>
        <div className="d-flex align-items-center">
          <Icon className="me-2" name={type} size={20} useImage={true} />
          <div className={styles.nameColumn}>
            <Text style={{ textTransform: "capitalize" }}>{type}</Text>
          </div>
        </div>
      </td>
      <td>
        <Text>{version}</Text>
        <Text
          style={{
            textTransform: "capitalize",
          }}
        >
          {environment}
        </Text>
      </td>
      <td>
        <DateTimeFormatter
          invalidDateText="Invalid Date"
          timestamp={createdAt}
        />
      </td>
      <td>{renderDescription()}</td>
      <td>
        <Text>Inactive</Text>
      </td>
      <td>
        <Dropdown
          menu={dropdownMenu}
          trigger={["click"]}
          placement="bottomRight"
          overlayClassName="release-dropdown-menu"
        >
          <div className={styles.actionButton}>
            <MoreOutlined style={{ fontSize: 18 }} />
          </div>
        </Dropdown>
      </td>
    </tr>
  );
};

export default ReleaseRow;
